/**
 * Account Card Component
 * 
 * Displays account information in a card format with navigation and action buttons.
 * 
 * Key Features:
 * - Account detail navigation (click to open account page)
 * - Notes navigation (notes button in bottom action bar)
 * - AI summarization (chat button in bottom action bar requests server-side account note analysis)
 * - Drag-to-chat integration: Drag account cards to chat panel to analyze account notes
 * - Delete account functionality (X button in top-right corner)
 * - Keyword highlighting for search results
 * - Type chip display (RLP/Team classification) in bottom action bar
 * - Light background with dark mode support
 * 
 * Layout:
 * - Header: Account name with delete (X) button in top-right
 * - Content: Account details in main area
 * - Footer: Tags and action buttons aligned to left with border separator
 * 
 * Drag-to-Chat Implementation:
 * - Cards are draggable only when account has notes (noteCount > 0)
 * - Drag transfers only a record reference and reveals Chat; only a completed Chat drop submits
 * - Visual feedback during drag (opacity change)
 * 
 * Props:
 * - account: Account object with account details and noteCount
 * - keywords: Array of strings for highlighting search matches
 * - onDelete: Callback function for account deletion
 */

import { FC, useState } from 'react';
import { useNavigate } from 'react-router';
import { Account } from '../../models/Account';
import { foundationSemantic } from '../../foundation';
import { highlightText } from '../../utils/highlightUtils';
import { getAccountCardDisplayFields } from '../../utils/accountUtils';
import { renderTypeChip } from '../../utils/typeChipUtils';
import { useEventBus } from '../../context/EventBusContext';
import { EVENTS } from '../../constants/eventConstants';
import { getCardAnalysisPrompt } from '../../utils/summaryUtils';
import { useAccountPermissions } from '../../hooks/usePermissions';
import PermissionIndicator from '../common/PermissionIndicator';
import { Button, Surface } from '../primitives';

interface AccountCardProps {
  account: Account;
  keywords: string[];
  onDelete: (id: number) => Promise<void>;
  showDeleteButton?: boolean; // Override permission checks for delete button visibility
}

const AccountCard: FC<AccountCardProps> = ({ account, keywords, onDelete, showDeleteButton }) => {
  const navigate = useNavigate();
  const { publish } = useEventBus();
  const [isDragging, setIsDragging] = useState(false);

  // Check permissions using the new hook
  const permissions = useAccountPermissions(account);

  // Determine if delete button should be shown (either explicitly set or based on permissions)
  const shouldShowDeleteButton = showDeleteButton ?? permissions.canDelete;

  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await onDelete(account.id);
    } catch (error) {
      console.error('Failed to delete account:', error);
    }
  };

  const handleNotesClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/accounts/${account.id}/notes`);
  };

  const sendNotesToChat = () => {
    publish(EVENTS.CHAT_OPEN, {
      prompt: getCardAnalysisPrompt({ accountId: account.id }),
      createNewSession: true,
    });
  };

  // Reveal Chat on drag intent; the Chat drop target owns submission.
  const handleDragStart = (e: React.DragEvent<HTMLDivElement>) => {
    if (account.noteCount === 0) {
      e.preventDefault();
      return;
    }
    // Set dragging state for visual feedback
    setIsDragging(true);
    e.dataTransfer.setData('application/json', JSON.stringify({ accountId: account.id }));
    e.dataTransfer.effectAllowed = 'copy';
    publish(EVENTS.CHAT_OPEN_FOR_DROP, {});
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  return (
    <Surface
      variant="interactive"
      className={`p-6 shadow-lg cursor-pointer ${isDragging ? 'opacity-50' : ''}`}
      onClick={() => navigate(`/accounts/${account.id}`)}
      draggable={account.noteCount > 0}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      title={account.noteCount > 0 ? 'Drag to chat to analyze notes' : 'No notes to analyze'}
    >
      <div className="flex flex-col h-full">
        <div className="flex justify-between items-start gap-2 mb-3">
          <h3 className={`text-sm font-semibold ${foundationSemantic.text.primary} flex-1`}>
            {highlightText(account.name, keywords)}
          </h3>
          <div className="flex items-center gap-1">
            {/* Permission indicator */}
            <PermissionIndicator
              canEdit={permissions.canEdit}
              description={permissions.description}
            />
            {/* Delete button - show based on prop or permission check */}
            {shouldShowDeleteButton && (
              <Button
                onClick={handleDelete}
                variant="icon"
                className="h-7 w-7 flex-shrink-0"
                title="Remove account"
                aria-label="Remove account"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className={`h-4 w-4 ${foundationSemantic.text.secondary}`} viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </Button>
            )}
          </div>
        </div>

        <div className="text-xs space-y-2 flex-1 mb-3">
          {getAccountCardDisplayFields().map(field => {
            const value = account[field.key];
            if (field.hideEmpty && !value) {
              return null;
            }

            const displayValue = value ?? '-';
            const needsHighlight = typeof value === 'string';

            return (
              <div key={field.key} className="flex justify-between items-start gap-4">
                <span className={`${foundationSemantic.text.secondary} shrink-0`}>{field.label}:</span>
                <span className={`${foundationSemantic.text.secondary} text-right ${field.key === 'streetAddress' || field.key === 'mailingAddress' ? 'break-words' : ''}`}>
                  {needsHighlight ? highlightText(displayValue.toString(), keywords) : displayValue}
                </span>
              </div>
            );
          })}
        </div>

        {/* Bottom section with tags left and actions right */}
        <div className="flex items-center justify-between pt-2">
          <div className="flex items-center gap-2">
            {renderTypeChip({ rlpCompanyId: account.rlpCompanyId, teamId: account.teamId })}
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={handleNotesClick}
              variant="icon"
              className="h-8 w-8"
              title="View notes"
              aria-label="View notes"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className={`h-4 w-4 ${foundationSemantic.text.secondary}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
              </svg>
            </Button>
            <Button
              onClick={(e) => { e.stopPropagation(); sendNotesToChat(); }}
              variant="icon"
              className="h-8 w-8"
              title="Ask AI about this account"
              aria-label="Ask AI about this account"
              disabled={account.noteCount === 0}
            >
              <svg className={`h-4 w-4 ${foundationSemantic.text.secondary}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </Button>
          </div>
        </div>
      </div>
    </Surface>
  );
};

export default AccountCard;
